// Touch controls, drawn by the host around the game (plan §6). GBA: D-pad, A, B, L, R, Start, Select. N64: analog
// stick, A, B, Z, L, R, Start, four C buttons. Plus the manifest's controls.extra buttons. The layout is set by
// CSS from the viewport and platform only, never from the game's resolution, so nothing moves when that changes.
//
// Every pointer is tracked on its own (multi-touch). A finger that starts on a button can slide to another one; a
// finger that starts on the stick or D-pad stays bound to it until it lifts.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { PadState } from '../../shared/protocol.ts';
import { controlsFor, extraId, stateFrom, type Extra } from '../lib/keyboard.ts';

type Bound =
  | { kind: 'btn'; ids: string[] }
  | { kind: 'dpad'; el: HTMLElement; ids: string[] }
  | { kind: 'stick'; el: HTMLElement; x: number; y: number };

const DPAD_IDS: Record<string, [string, string, string, string]> = {
  gba: ['Up', 'Down', 'Left', 'Right'],
  n64: ['DUp', 'DDown', 'DLeft', 'DRight'],
};

function dpadDirs(el: HTMLElement, x: number, y: number, platform: string): string[] {
  const r = el.getBoundingClientRect();
  const dx = (x - (r.left + r.width / 2)) / (r.width / 2), dy = (y - (r.top + r.height / 2)) / (r.height / 2);
  const [u, d, l, rt] = DPAD_IDS[platform] ?? DPAD_IDS.gba;
  const out: string[] = [];
  const t = 0.35; // dead centre; diagonals when both axes pass it
  if (dy < -t) out.push(u);
  if (dy > t) out.push(d);
  if (dx < -t) out.push(l);
  if (dx > t) out.push(rt);
  return out;
}

function stickVec(el: HTMLElement, x: number, y: number): [number, number] {
  const r = el.getBoundingClientRect();
  const rad = r.width / 2;
  let dx = (x - (r.left + rad)) / rad, dy = -(y - (r.top + r.height / 2)) / rad;
  const len = Math.hypot(dx, dy);
  if (len > 1) { dx /= len; dy /= len; }
  return [Math.round(dx * 127), Math.round(dy * 127)];
}

export interface TouchProps {
  platform: string;
  extra: Extra[];
  onChange: (s: PadState) => void;
}

function Btn({ id, label, on, cls = '' }: { id: string; label: string; on: Set<string>; cls?: string }) {
  return <div class={`tb ${cls}${on.has(id) ? ' on' : ''}`} data-touch={id}>{label}</div>;
}

export function TouchControls({ platform, extra, onChange }: TouchProps) {
  const controls = useMemo(() => controlsFor(platform, extra), [platform, extra]);
  const pointers = useRef(new Map<number, Bound>());
  const [on, setOn] = useState<Set<string>>(new Set());
  const [knob, setKnob] = useState<[number, number]>([0, 0]);
  const last = useRef('');

  const emit = () => {
    const ids = new Set<string>();
    let sx = 0, sy = 0;
    for (const b of pointers.current.values()) {
      if (b.kind === 'stick') { sx = b.x; sy = b.y; } else for (const id of b.ids) ids.add(id);
    }
    const s = stateFrom(controls, ids);
    s.sx = sx; s.sy = sy;
    const k = `${s.buttons},${s.sx},${s.sy}`;
    if (k !== last.current) { last.current = k; onChange(s); }
    setOn(ids);
    setKnob([sx, sy]);
  };

  const hit = (x: number, y: number): HTMLElement | null =>
    (document.elementFromPoint(x, y) as HTMLElement | null)?.closest<HTMLElement>('[data-touch]') ?? null;

  const down = (e: PointerEvent) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-touch]');
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    if (!el) { pointers.current.set(e.pointerId, { kind: 'btn', ids: [] }); return; }
    const t = el.dataset.touch!;
    if (t === 'stick') { const [x, y] = stickVec(el, e.clientX, e.clientY); pointers.current.set(e.pointerId, { kind: 'stick', el, x, y }); }
    else if (t === 'dpad') pointers.current.set(e.pointerId, { kind: 'dpad', el, ids: dpadDirs(el, e.clientX, e.clientY, platform) });
    else pointers.current.set(e.pointerId, { kind: 'btn', ids: [t] });
    emit();
  };
  const move = (e: PointerEvent) => {
    const b = pointers.current.get(e.pointerId);
    if (!b) return;
    e.preventDefault();
    if (b.kind === 'stick') { [b.x, b.y] = stickVec(b.el, e.clientX, e.clientY); }
    else if (b.kind === 'dpad') b.ids = dpadDirs(b.el, e.clientX, e.clientY, platform);
    else {
      const el = hit(e.clientX, e.clientY);
      const t = el?.dataset.touch;
      b.ids = t && t !== 'stick' && t !== 'dpad' ? [t] : [];
    }
    emit();
  };
  const up = (e: PointerEvent) => {
    if (!pointers.current.delete(e.pointerId)) return;
    emit();
  };

  // release everything if the controls go away (menu, rotation re-layout, unmount)
  useEffect(() => () => { pointers.current.clear(); onChange({ buttons: 0, sx: 0, sy: 0 }); }, []);

  const handlers = {
    onPointerDown: down, onPointerMove: move, onPointerUp: up, onPointerCancel: up,
    onContextMenu: (e: Event) => e.preventDefault(),
  };
  const dpad = (
    <div class="dpad" data-touch="dpad">
      {(DPAD_IDS[platform] ?? DPAD_IDS.gba).map((id, i) => (
        <span key={id} class={`arm arm-${['u', 'd', 'l', 'r'][i]}${on.has(id) ? ' on' : ''}`} />
      ))}
    </div>
  );
  const extras = extra.filter((x) => x.bit >= 16 && x.bit <= 31).map((x) => (
    <Btn key={x.bit} id={extraId(x.bit)} label={x.label} on={on} cls="small extra" />
  ));

  if (platform === 'n64') {
    return (
      <>
        <div class="tc tc-left" data-cluster="left" {...handlers}>
          <div class="shoulders"><Btn id="L" label="L" on={on} cls="shoulder" /><Btn id="Z" label="Z" on={on} cls="shoulder" /></div>
          <div class="stick" data-touch="stick">
            <div class="knob" style={{ transform: `translate(${(knob[0] / 127) * 50}%, ${(-knob[1] / 127) * 50}%)` }} />
          </div>
        </div>
        <div class="tc tc-mid" data-cluster="mid" {...handlers}>
          <Btn id="Start" label="Start" on={on} cls="small" />
          {extras}
        </div>
        <div class="tc tc-right" data-cluster="right" {...handlers}>
          <div class="shoulders right"><Btn id="R" label="R" on={on} cls="shoulder" /></div>
          <div class="n64-face">
            <Btn id="CUp" label="▲" on={on} cls="c c-u" /><Btn id="CLeft" label="◀" on={on} cls="c c-l" />
            <Btn id="CRight" label="▶" on={on} cls="c c-r" /><Btn id="CDown" label="▼" on={on} cls="c c-d" />
            <Btn id="B" label="B" on={on} cls="face b" /><Btn id="A" label="A" on={on} cls="face a" />
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <div class="tc tc-left" data-cluster="left" {...handlers}>
        <div class="shoulders"><Btn id="L" label="L" on={on} cls="shoulder" /></div>
        {dpad}
      </div>
      <div class="tc tc-mid" data-cluster="mid" {...handlers}>
        <Btn id="Select" label="Select" on={on} cls="small" />
        <Btn id="Start" label="Start" on={on} cls="small" />
        {extras}
      </div>
      <div class="tc tc-right" data-cluster="right" {...handlers}>
        <div class="shoulders right"><Btn id="R" label="R" on={on} cls="shoulder" /></div>
        <div class="gba-face"><Btn id="B" label="B" on={on} cls="face b" /><Btn id="A" label="A" on={on} cls="face a" /></div>
      </div>
    </>
  );
}

/** "auto": shown on touch screens (coarse pointer or touch points) or once a touch has been seen. */
export type TouchMode = 'auto' | 'on' | 'off';

export function touchLikely(): boolean {
  try {
    return matchMedia('(pointer: coarse)').matches || (navigator.maxTouchPoints ?? 0) > 0;
  } catch { return false; }
}
